<template>
    <div class="whiteboard-page">
        <div class="canvas-wrapper" ref="canvasWrapper">
            <canvas ref="canvasEl"></canvas>
        </div>

        <div class="float-bar">
            <div class="bar-section">
                <button
                    v-for="tool in tools"
                    :key="tool.id"
                    class="tool-btn"
                    :class="{ active: activeTool === tool.id }"
                    @click="setTool(tool.id)"
                    :title="tool.label"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <template v-if="tool.id === 'brush'">
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                            <path d="m15 5 4 4"/>
                        </template>
                        <template v-else-if="tool.id === 'eraser'">
                            <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/>
                            <path d="M22 21H7"/>
                            <path d="m5 11 9 9"/>
                        </template>
                        <template v-else-if="tool.id === 'rect'">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                        </template>
                        <template v-else-if="tool.id === 'circle'">
                            <circle cx="12" cy="12" r="10"/>
                        </template>
                        <template v-else-if="tool.id === 'line'">
                            <line x1="5" y1="19" x2="19" y2="5"/>
                        </template>
                        <template v-else-if="tool.id === 'text'">
                            <polyline points="4 7 4 4 20 4 20 7"/>
                            <line x1="9" y1="20" x2="15" y2="20"/>
                            <line x1="12" y1="4" x2="12" y2="20"/>
                        </template>
                    </svg>
                </button>
            </div>

            <div class="bar-divider"></div>

            <div class="bar-section colors-section">
                <button
                    v-for="c in presetColors"
                    :key="c"
                    class="color-dot"
                    :class="{ active: strokeColor === c }"
                    :style="{ background: c }"
                    @click="selectColor(c)"
                ></button>
                <label class="color-dot custom-color" :style="{ background: strokeColor }" title="Custom color">
                    <input type="color" v-model="strokeColor" @input="updateBrush" />
                </label>
            </div>

            <div class="bar-divider"></div>

            <div class="bar-section">
                <div class="size-control">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <input type="range" v-model="strokeWidth" min="1" max="30" @input="updateBrush" class="size-slider" />
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="6"/>
                    </svg>
                </div>
            </div>

            <div class="bar-divider"></div>

            <div class="bar-section">
                <button class="tool-btn" @click="undo" title="Undo">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
                    </svg>
                </button>
                <button class="tool-btn" @click="clearCanvas" title="Clear">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                    </svg>
                </button>
                <button class="tool-btn" @click="exportImage" title="Export PNG">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                </button>
            </div>

            <div class="bar-divider"></div>

            <div class="bar-section">
                <button class="tool-btn" @click="newCanvas" title="New Canvas (Ctrl+N)">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                </button>
            </div>

            <div class="bar-divider"></div>

            <div class="bar-section">
                <template v-if="isLoggedIn">
                    <button class="tool-btn accent" @click="openSaveDialog" title="Save">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                        </svg>
                    </button>
                    <button class="tool-btn" @click="loadMyBoards" title="My Boards">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                        </svg>
                    </button>
                </template>
                <template v-else>
                    <button class="tool-btn" @click="goToLogin" title="Login to save">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
                        </svg>
                    </button>
                </template>
            </div>
        </div>

        <el-dialog v-model="saveDialogVisible" title="Save Whiteboard" width="400px">
            <el-input v-model="boardName" placeholder="Board name" maxlength="50" show-word-limit />
            <template #footer>
                <el-button @click="saveDialogVisible = false">Cancel</el-button>
                <el-button type="primary" @click="saveBoard" :loading="saving">Save</el-button>
            </template>
        </el-dialog>

        <el-drawer v-model="drawerVisible" title="My Boards" size="360px">
            <div v-if="loadingBoards" class="drawer-loading">Loading...</div>
            <div v-else-if="boards.length === 0" class="drawer-empty">No saved boards</div>
            <div v-else class="board-list">
                <div
                    v-for="board in boards"
                    :key="board.id"
                    class="board-item"
                    @click="loadBoard(board.id)"
                >
                    <div class="board-info">
                        <div class="board-name">{{ board.name }}</div>
                        <div class="board-date">{{ formatDate(board.updatedAt) }}</div>
                    </div>
                    <el-button
                        size="small"
                        type="danger"
                        link
                        @click.stop="deleteBoard(board)"
                    >
                        Delete
                    </el-button>
                </div>
            </div>
        </el-drawer>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Canvas, PencilBrush, Rect, Circle, Line, IText } from 'fabric';
import { isAuthenticated } from '../../utils/auth';
import { whiteboardApi } from '../../api/whiteboard';

export default {
    name: 'WhiteBoard',
    setup() {
        const router = useRouter();
        const canvasEl = ref(null);
        const canvasWrapper = ref(null);
        let canvas = null;
        let historyStack = [];
        let historyIndex = -1;
        let isRestoring = false;
        let lineDrawing = null;

        const activeTool = ref('brush');
        const strokeColor = ref('#1d1d1f');
        const strokeWidth = ref(3);
        const isLoggedIn = ref(isAuthenticated());

        const presetColors = [
            '#1d1d1f', '#6e6e73', '#ff3b30', '#ff9500',
            '#ffcc00', '#34c759', '#007aff', '#af52de',
        ];

        const saveDialogVisible = ref(false);
        const boardName = ref('');
        const saving = ref(false);
        const currentBoardId = ref(null);

        const drawerVisible = ref(false);
        const boards = ref([]);
        const loadingBoards = ref(false);

        const tools = [
            { id: 'brush', label: 'Pen' },
            { id: 'eraser', label: 'Eraser' },
            { id: 'rect', label: 'Rectangle' },
            { id: 'circle', label: 'Circle' },
            { id: 'line', label: 'Line' },
            { id: 'text', label: 'Text' },
        ];

        const initCanvas = () => {
            const wrapper = canvasWrapper.value;
            if (!wrapper || !canvasEl.value) return;

            canvas = new Canvas(canvasEl.value, {
                width: wrapper.clientWidth,
                height: wrapper.clientHeight,
                backgroundColor: '#fafafa',
                isDrawingMode: true,
            });

            canvas.freeDrawingBrush = new PencilBrush(canvas);
            canvas.freeDrawingBrush.color = strokeColor.value;
            canvas.freeDrawingBrush.width = strokeWidth.value;

            canvas.on('object:modified', saveHistory);
            canvas.on('object:added', () => {
                if (!isRestoring) saveHistory();
            });

            canvas.on('mouse:down', handleMouseDown);
            canvas.on('mouse:move', handleMouseMove);
            canvas.on('mouse:up', handleMouseUp);

            saveHistory();
        };

        const saveHistory = () => {
            if (isRestoring) return;
            historyStack = historyStack.slice(0, historyIndex + 1);
            historyStack.push(JSON.stringify(canvas.toJSON()));
            historyIndex = historyStack.length - 1;
            if (historyStack.length > 50) {
                historyStack.shift();
                historyIndex--;
            }
        };

        const undo = () => {
            if (historyIndex <= 0) return;
            historyIndex--;
            isRestoring = true;
            canvas.loadFromJSON(historyStack[historyIndex], () => {
                canvas.renderAll();
                isRestoring = false;
            });
        };

        const redo = () => {
            if (historyIndex >= historyStack.length - 1) return;
            historyIndex++;
            isRestoring = true;
            canvas.loadFromJSON(historyStack[historyIndex], () => {
                canvas.renderAll();
                isRestoring = false;
            });
        };

        const handleMouseDown = (opt) => {
            if (activeTool.value !== 'line') return;
            const pointer = canvas.getScenePoint(opt.e);
            lineDrawing = new Line(
                [pointer.x, pointer.y, pointer.x, pointer.y],
                { stroke: strokeColor.value, strokeWidth: strokeWidth.value, selectable: true, evented: true }
            );
            isRestoring = true;
            canvas.add(lineDrawing);
            isRestoring = false;
        };

        const handleMouseMove = (opt) => {
            if (!lineDrawing || activeTool.value !== 'line') return;
            const pointer = canvas.getScenePoint(opt.e);
            lineDrawing.set({ x2: pointer.x, y2: pointer.y });
            canvas.renderAll();
        };

        const handleMouseUp = () => {
            if (!lineDrawing) return;
            lineDrawing = null;
        };

        const selectColor = (c) => {
            strokeColor.value = c;
            updateBrush();
        };

        const setTool = (tool) => {
            activeTool.value = tool;
            canvas.isDrawingMode = false;
            canvas.defaultCursor = 'default';

            const isDrawing = tool === 'brush' || tool === 'eraser';

            canvas.forEachObject(obj => {
                obj.selectable = !isDrawing;
                obj.evented = !isDrawing;
            });

            canvas.selection = !isDrawing;

            if (tool === 'brush') {
                canvas.isDrawingMode = true;
                canvas.freeDrawingBrush = new PencilBrush(canvas);
                canvas.freeDrawingBrush.color = strokeColor.value;
                canvas.freeDrawingBrush.width = strokeWidth.value;
            } else if (tool === 'eraser') {
                canvas.isDrawingMode = true;
                canvas.freeDrawingBrush = new PencilBrush(canvas);
                canvas.freeDrawingBrush.color = '#fafafa';
                canvas.freeDrawingBrush.width = strokeWidth.value * 3;
            } else if (tool === 'rect') {
                addRect();
            } else if (tool === 'circle') {
                addCircle();
            } else if (tool === 'line') {
                canvas.defaultCursor = 'crosshair';
            } else if (tool === 'text') {
                addText();
            }
        };

        const addRect = () => {
            const rect = new Rect({
                left: 100, top: 100, width: 150, height: 100,
                fill: 'transparent', stroke: strokeColor.value,
                strokeWidth: strokeWidth.value, selectable: true, evented: true,
            });
            canvas.add(rect);
            canvas.setActiveObject(rect);
        };

        const addCircle = () => {
            const circle = new Circle({
                left: 150, top: 150, radius: 60,
                fill: 'transparent', stroke: strokeColor.value,
                strokeWidth: strokeWidth.value, selectable: true, evented: true,
            });
            canvas.add(circle);
            canvas.setActiveObject(circle);
        };

        const addText = () => {
            const text = new IText('Text', {
                left: 150, top: 150, fontSize: 24,
                fill: strokeColor.value, fontFamily: 'sans-serif',
                selectable: true, evented: true,
            });
            canvas.add(text);
            canvas.setActiveObject(text);
        };

        const updateBrush = () => {
            if (!canvas) return;
            if (activeTool.value === 'brush') {
                canvas.freeDrawingBrush.color = strokeColor.value;
                canvas.freeDrawingBrush.width = strokeWidth.value;
            } else if (activeTool.value === 'eraser') {
                canvas.freeDrawingBrush.width = strokeWidth.value * 3;
            }
        };

        const clearCanvas = () => {
            if (!canvas) return;
            canvas.clear();
            canvas.backgroundColor = '#fafafa';
            canvas.renderAll();
            saveHistory();
        };

        const exportImage = () => {
            if (!canvas) return;
            const dataURL = canvas.toDataURL({ format: 'png', quality: 1 });
            const link = document.createElement('a');
            link.download = `whiteboard_${Date.now()}.png`;
            link.href = dataURL;
            link.click();
        };

        const newCanvas = () => {
            if (!canvas) return;
            canvas.clear();
            canvas.backgroundColor = '#fafafa';
            canvas.renderAll();
            currentBoardId.value = null;
            historyStack = [];
            historyIndex = -1;
            saveHistory();
        };

        const goToLogin = () => router.push('/userLogin');

        const openSaveDialog = () => {
            boardName.value = '';
            saveDialogVisible.value = true;
        };

        const saveBoard = async () => {
            if (!boardName.value.trim()) {
                ElMessage.warning('Please enter a board name');
                return;
            }
            saving.value = true;
            try {
                const data = JSON.stringify(canvas.toJSON());
                if (currentBoardId.value) {
                    await whiteboardApi.updateBoard(currentBoardId.value, {
                        name: boardName.value.trim(), data,
                    });
                    ElMessage.success('Board updated');
                } else {
                    const resp = await whiteboardApi.createBoard({
                        name: boardName.value.trim(), data,
                    });
                    currentBoardId.value = resp.data.id;
                    ElMessage.success('Board saved');
                }
                saveDialogVisible.value = false;
            } catch (e) {
                console.error('Save failed:', e);
                ElMessage.error('Save failed');
            } finally {
                saving.value = false;
            }
        };

        const loadMyBoards = async () => {
            drawerVisible.value = true;
            loadingBoards.value = true;
            try {
                const resp = await whiteboardApi.listBoards();
                boards.value = resp.data || [];
            } catch (e) {
                console.error('Load boards failed:', e);
                ElMessage.error('Failed to load boards');
            } finally {
                loadingBoards.value = false;
            }
        };

        const loadBoard = async (id) => {
            try {
                const resp = await whiteboardApi.getBoard(id);
                const board = resp.data;
                isRestoring = true;
                canvas.loadFromJSON(board.data, () => {
                    canvas.renderAll();
                    isRestoring = false;
                    currentBoardId.value = board.id;
                    historyStack = [board.data];
                    historyIndex = 0;
                });
                drawerVisible.value = false;
                ElMessage.success(`Loaded: ${board.name}`);
            } catch (e) {
                console.error('Load board failed:', e);
                ElMessage.error('Failed to load board');
            }
        };

        const deleteBoard = async (board) => {
            try {
                await ElMessageBox.confirm(
                    `Delete "${board.name}"?`, 'Confirm',
                    { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' }
                );
            } catch { return; }
            try {
                await whiteboardApi.deleteBoard(board.id);
                boards.value = boards.value.filter(b => b.id !== board.id);
                if (currentBoardId.value === board.id) currentBoardId.value = null;
                ElMessage.success('Deleted');
            } catch (e) {
                console.error('Delete failed:', e);
                ElMessage.error('Delete failed');
            }
        };

        const formatDate = (ts) => {
            if (!ts) return '';
            return new Date(ts).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            });
        };

        const handleResize = () => {
            if (!canvas || !canvasWrapper.value) return;
            const wrapper = canvasWrapper.value;
            canvas.setDimensions({ width: wrapper.clientWidth, height: wrapper.clientHeight });
            canvas.renderAll();
        };

        const deleteSelected = () => {
            if (!canvas) return;
            const active = canvas.getActiveObjects();
            if (active.length === 0) return;
            active.forEach(obj => canvas.remove(obj));
            canvas.discardActiveObject();
            canvas.renderAll();
        };

        const handleKeyboard = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            const key = e.key.toLowerCase();
            const ctrl = e.ctrlKey || e.metaKey;

            if ((key === 'delete' || key === 'backspace') && !ctrl) {
                e.preventDefault();
                deleteSelected();
            } else if (key === 'z' && ctrl && !e.shiftKey) {
                e.preventDefault();
                undo();
            } else if (key === 'z' && ctrl && e.shiftKey) {
                e.preventDefault();
                redo();
            } else if (key === 's' && ctrl) {
                e.preventDefault();
                if (isLoggedIn.value) openSaveDialog();
            } else if (key === 'n' && ctrl) {
                e.preventDefault();
                newCanvas();
            } else if (key === 'b' && !ctrl) {
                setTool('brush');
            } else if (key === 'e' && !ctrl) {
                setTool('eraser');
            } else if (key === 'r' && !ctrl) {
                setTool('rect');
            } else if (key === 'o' && !ctrl) {
                setTool('circle');
            } else if (key === 'l' && !ctrl) {
                setTool('line');
            } else if (key === 't' && !ctrl) {
                setTool('text');
            }
        };

        const updateAuthState = () => { isLoggedIn.value = isAuthenticated(); };

        onMounted(() => {
            nextTick(() => initCanvas());
            window.addEventListener('resize', handleResize);
            window.addEventListener('keydown', handleKeyboard);
            window.addEventListener('storage', (e) => {
                if (e.key === 'xtool_token') updateAuthState();
            });
            window.addEventListener('auth-change', updateAuthState);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('keydown', handleKeyboard);
            if (canvas) canvas.dispose();
        });

        return {
            canvasEl, canvasWrapper, activeTool, strokeColor, strokeWidth,
            isLoggedIn, tools, presetColors,
            saveDialogVisible, boardName, saving,
            drawerVisible, boards, loadingBoards,
            setTool, selectColor, updateBrush, clearCanvas, undo, exportImage,
            goToLogin, openSaveDialog, saveBoard, loadMyBoards, loadBoard,
            deleteBoard, formatDate,
        };
    }
};
</script>

<style scoped>
.whiteboard-page {
    position: relative;
    height: calc(100vh - 60px);
    overflow: hidden;
}

.canvas-wrapper {
    width: 100%;
    height: 100%;
    background-color: #fafafa;
    background-image: radial-gradient(circle, #d8d8d8 0.8px, transparent 0.8px);
    background-size: 24px 24px;
}

.canvas-wrapper canvas {
    display: block;
}

.float-bar {
    position: absolute;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(20px) saturate(1.6);
    -webkit-backdrop-filter: blur(20px) saturate(1.6);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
    z-index: 10;
}

.bar-section {
    display: flex;
    align-items: center;
    gap: 2px;
}

.bar-divider {
    width: 1px;
    height: 24px;
    background: rgba(0, 0, 0, 0.08);
    margin: 0 6px;
    flex-shrink: 0;
}

.tool-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #6e6e73;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
}

.tool-btn:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #1d1d1f;
}

.tool-btn.active {
    background: #1d1d1f;
    color: #fff;
}

.tool-btn.accent {
    color: #007aff;
}

.tool-btn.accent:hover {
    background: rgba(0, 122, 255, 0.1);
}

.color-dot {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
    padding: 0;
    outline: none;
}

.color-dot:hover {
    transform: scale(1.15);
}

.color-dot.active {
    border-color: #1d1d1f;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
}

.custom-color {
    position: relative;
    overflow: hidden;
    background-image: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
}

.custom-color input[type="color"] {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    border: none;
}

.size-control {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #8e8e93;
}

.size-slider {
    width: 80px;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    outline: none;
}

.size-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #1d1d1f;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.drawer-loading,
.drawer-empty {
    text-align: center;
    padding: 40px 0;
    color: var(--text-tertiary);
    font-size: var(--text-sm);
}

.board-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
}

.board-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-md);
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background var(--transition-fast);
    border: 1px solid var(--border-light);
}

.board-item:hover {
    background: var(--bg-hover);
}

.board-name {
    font-size: var(--text-base);
    font-weight: 500;
    color: var(--text-primary);
}

.board-date {
    font-size: var(--text-xs);
    color: var(--text-tertiary);
    margin-top: 2px;
}

@media (prefers-color-scheme: dark) {
    .canvas-wrapper {
        background-color: #1a1a1c;
        background-image: radial-gradient(circle, #3a3a3c 0.8px, transparent 0.8px);
    }

    .float-bar {
        background: rgba(44, 44, 46, 0.82);
        border-color: rgba(255, 255, 255, 0.06);
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3), 0 1px 4px rgba(0, 0, 0, 0.2);
    }

    .tool-btn { color: #98989d; }
    .tool-btn:hover { background: rgba(255, 255, 255, 0.08); color: #f5f5f7; }
    .tool-btn.active { background: #f5f5f7; color: #1d1d1f; }
    .tool-btn.accent { color: #0a84ff; }
    .tool-btn.accent:hover { background: rgba(10, 132, 255, 0.15); }

    .bar-divider { background: rgba(255, 255, 255, 0.08); }

    .color-dot.active { border-color: #f5f5f7; }

    .size-control { color: #98989d; }
    .size-slider { background: rgba(255, 255, 255, 0.1); }
    .size-slider::-webkit-slider-thumb { background: #f5f5f7; border-color: #2c2c2e; }
}
</style>
